import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { AlertCircle, Ban, Info, Paperclip, Pencil, Search, Trash2 } from "lucide-react"
import { useEffect, useRef, useState } from "react"

import { api, ErrorApi } from "@/api/cliente"
import { Exportar } from "@/componentes/exportar/Exportar"
import { Boton } from "@/componentes/ui/Boton"
import { useConfirmar } from "@/componentes/ui/Confirmar"
import { Insignia } from "@/componentes/ui/Insignia"
import {
  limitesDe,
  nombreDe,
  type Periodo,
  SelectorPeriodo,
} from "@/componentes/ui/SelectorPeriodo"
import { Cabecera, Cuerpo, Fila, Tabla, Td, Th } from "@/componentes/ui/Tabla"
import { type ArchivoParaVer, VistaPreviaArchivo } from "@/componentes/ui/VistaPreviaArchivo"
import { tienePermiso } from "@/lib/autorizacion"
import { comoFecha, comoFechaHora } from "@/lib/fechas"
import { usarSesion } from "@/lib/sesion"
import { DetalleMovimiento } from "./DetalleMovimiento"
import { DialogoAnular } from "./DialogoAnular"
import { DialogoEditarMovimiento } from "./DialogoEditarMovimiento"
import { comoMonto, conSaldoCorrido, monto } from "./dinero"
import type { Cuenta, Movimiento } from "./tipos"

/** Cuántos trae de primeras, y cuántos añade cada «ver más». */
const POR_TANDA = 200

/**
 * El techo: el `limite` del servidor admite 500 como máximo.
 *
 * ⚠ **Por eso el «ver más» se acaba**, y cuando se acaba se dice qué hacer: elegir un
 * mes o irse a «Movimientos». Un botón que pide 600 y recibe un 422 es peor que no
 * tener botón.
 */
const TOPE_SERVIDOR = 500

/**
 * El extracto de una cuenta, con el saldo después de cada movimiento.
 *
 * ⚠ **La columna que justifica la pantalla es el SALDO CORRIDO**, como en
 * len-platform. Una lista de importes obliga a sumar de cabeza para saber si el
 * descubierto del martes fue antes o después del depósito del lunes, que es justo lo
 * que se pregunta cuando algo no cuadra contra el banco. Se calcula hacia atrás desde
 * el saldo de hoy (`conSaldoCorrido`), así que es exacto aunque no estén todas.
 *
 * ⚠ **Lo anulado se queda, tachado y sin saldo.** El banco también conserva las dos
 * operaciones; un extracto que las borra tiene el saldo bien y la historia falsa.
 *
 * ⚠ **Se filtra por mes y por texto, y el tope crece a tandas.** El extracto de una
 * cuenta con año y medio de operación no se recorre bajando: se pregunta por septiembre,
 * o por «comisión». Los dos filtros son del SERVIDOR — nunca se traen todas las filas
 * para cortarlas aquí.
 *
 * ⚠ **CON FILTROS NO HAY SALDO CORRIDO, y se dice por qué.** El saldo se calcula
 * restando hacia atrás desde el de hoy, y eso sólo vale si las filas son las más
 * recientes SEGUIDAS. Con un mes pasado o una búsqueda, entre dos filas quedan
 * movimientos que no se ven: el número saldría impecable en su formato y falso en su
 * importe, que es la peor de las dos maneras de estar mal.
 *
 * ⚠ **Pulsar una fila abre su ficha** (`DetalleMovimiento`): de dónde sale —la orden
 * que paga, el cambio de recursos que lo explica— y su soporte en vista rápida. El de
 * un pago es el comprobante de la ORDEN, que la fila no puede enseñar como clip.
 *
 * Va en su propia tarjeta, debajo de la de la cuenta, como en len-platform: con filas,
 * la tabla ya es una tarjeta (`Tabla`); sin ellas, el aviso va dentro de una.
 */
export function Extracto({
  cuenta,
  saldo,
  divisa,
}: {
  cuenta: Cuenta
  /** El saldo de hoy, del resumen: de ahí parte el saldo corrido. */
  saldo: string
  divisa: string
}) {
  const usuario = usarSesion((e) => e.usuario)
  const clienteQuery = useQueryClient()
  const [confirmacion, confirmar] = useConfirmar()
  const puedeAnular = tienePermiso(usuario, "tesoreria:anular")
  const puedeEditar = tienePermiso(usuario, "tesoreria:registrar")
  // ⚠ Borra de verdad, no anula. Sólo el superadministrador lo tiene.
  const puedeEliminar = tienePermiso(usuario, "tesoreria:eliminar_movimiento")
  const [anulando, setAnulando] = useState<Movimiento | null>(null)
  const [editando, setEditando] = useState<Movimiento | null>(null)
  const [viendo, setViendo] = useState<ArchivoParaVer | null>(null)
  const [abierto, setAbierto] = useState<Movimiento | null>(null)
  const [texto, setTexto] = useState("")
  const [buscar, setBuscar] = useState("")
  // Arranca en TODOS: el extracto se abre para ver lo último que pasó por la cuenta, y
  // lo último no siempre es de este mes. El mes sirve para ir hacia atrás.
  const [periodo, setPeriodo] = useState<Periodo>(null)
  const [tope, setTope] = useState(POR_TANDA)
  // La caja de la tabla: lo que se exporta es lo que se está viendo.
  const tablaExtracto = useRef<HTMLDivElement | null>(null)

  // Se busca al dejar de teclear: la búsqueda es del servidor, y una petición por
  // pulsación haría parpadear el extracto mientras se escribe.
  useEffect(() => {
    const espera = window.setTimeout(() => setBuscar(texto.trim()), 300)
    return () => window.clearTimeout(espera)
  }, [texto])

  /**
   * Borra el movimiento de verdad, sin dejar fila.
   *
   * ⚠ **No es la forma de corregir una operación real**: para eso está anular, que
   * deja la fila tachada con su motivo y el extracto explicando el saldo. Esto existe
   * para limpiar las pruebas, y por eso pregunta antes y lo tiene un solo permiso.
   */
  const borrar = useMutation({
    mutationFn: async (movimiento: Movimiento) => {
      const seguro = await confirmar({
        titulo: "Borrar el movimiento",
        mensaje:
          `Se borra «${movimiento.concepto}» sin dejar rastro en el extracto. ` +
          "Si es la mitad de una transferencia anulada, se borra también la otra mitad, " +
          "en la otra cuenta. Para corregir una operación real, anúlala en vez de borrarla.",
        aceptar: "Borrar",
        peligro: true,
      })
      if (!seguro) return
      await api.delete(`/api/v1/transacciones/${movimiento.id}`)
    },
    onSuccess: () => {
      clienteQuery.invalidateQueries({ queryKey: ["movimientos"] })
      clienteQuery.invalidateQueries({ queryKey: ["resumen-cuenta"] })
    },
  })

  const limites = limitesDe(periodo)
  const hayFiltros = buscar !== "" || periodo !== null
  const parametros = new URLSearchParams({ cuenta_id: String(cuenta.id), limite: String(tope) })
  if (limites) {
    parametros.set("desde", limites.desde)
    parametros.set("hasta", limites.hasta)
  }
  if (buscar) parametros.set("buscar", buscar)

  const movimientos = useQuery({
    queryKey: ["movimientos", `extracto-${cuenta.id}`, { buscar, periodo, tope }],
    queryFn: () => api.get<Movimiento[]>(`/api/v1/transacciones?${parametros.toString()}`),
    // Sin esto, cada letra y cada cambio de mes vaciaban la tarjeta entera a «Cargando
    // el extracto…». Así se queda lo anterior hasta que llega lo nuevo.
    placeholderData: keepPreviousData,
  })

  const barra = (
    <div className="mb-3 flex flex-wrap items-center gap-2">
      {/* El mes primero: es el filtro que acota una lista que crece sin parar. */}
      <SelectorPeriodo
        valor={periodo}
        onCambiar={(p) => {
          setPeriodo(p)
          setTope(POR_TANDA)
        }}
        etiqueta="Mes del extracto"
      />
      <label className="relative min-w-[12rem] flex-1 sm:max-w-xs">
        <span className="sr-only">Buscar por concepto o referencia</span>
        <Search
          size={16}
          className="pointer-events-none absolute top-1/2 left-2.5 -translate-y-1/2"
          style={{ color: "var(--fg-muted)" }}
          aria-hidden={true}
        />
        <input
          type="search"
          value={texto}
          onChange={(e) => {
            setTexto(e.target.value)
            setTope(POR_TANDA)
          }}
          placeholder="Concepto o referencia…"
          className="campo w-full pl-8"
        />
      </label>
      {(movimientos.data?.length ?? 0) > 0 && (
        <span className="ml-auto">
          <Exportar
            titulo={`Extracto de ${cuenta.nombre}`}
            etiqueta="Exportar extracto"
            subtitulo={[
              `Cuenta ${cuenta.nombre} (${divisa})`,
              nombreDe(periodo),
              buscar ? `Búsqueda «${buscar}»` : null,
            ]
              .filter(Boolean)
              .join(" · ")}
            contenido={tablaExtracto}
          />
        </span>
      )}
    </div>
  )

  if (movimientos.isPending) {
    return (
      <div className="tarjeta text-sm" style={{ color: "var(--fg-muted)" }}>
        Cargando el extracto…
      </div>
    )
  }
  if (movimientos.isError) {
    return (
      <div className="tarjeta">
        {barra}
        <p role="alert" className="text-sm" style={{ color: "var(--destructive)" }}>
          No se pudo cargar el extracto
        </p>
      </div>
    )
  }
  if (movimientos.data.length === 0) {
    return (
      // Un aviso y no un vacío con un icono grande: la cuenta existe y está bien, lo que
      // le falta es su primer movimiento, y se dice de dónde va a venir.
      <div className="tarjeta">
        {barra}
        <div
          className="flex items-start gap-3 rounded-[var(--radius-control)] border p-3"
          style={{
            borderColor: "color-mix(in oklab, var(--info) 35%, var(--border))",
            backgroundColor: "color-mix(in oklab, var(--info) 10%, var(--surface))",
          }}
        >
          <Info
            size={16}
            aria-hidden={true}
            className="mt-0.5 shrink-0"
            style={{ color: "var(--info)" }}
          />
          <div>
            {/* ⚠ El vacío de un filtro no es el vacío de la cuenta. Decir «no tiene
                movimientos» con un mes puesto hace pensar que se perdieron. */}
            <p className="text-sm font-medium">
              {hayFiltros
                ? "Ningún movimiento con esos filtros"
                : "Esta cuenta no tiene movimientos"}
            </p>
            <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
              {hayFiltros
                ? "Prueba con otro mes, o quita la búsqueda para ver el extracto completo."
                : "Los pagos y cobros que la usen aparecerán aquí, y se pueden registrar movimientos a mano."}
            </p>
          </div>
        </div>
      </div>
    )
  }

  // ⚠ Con filtros, `saldoDespues` es `null` en TODAS, y arriba está el motivo. No es
  // que falte el dato: es que con una parte de los movimientos no existe.
  const lineas = hayFiltros
    ? movimientos.data.map((m) => ({ ...m, saldoDespues: null }))
    : conSaldoCorrido(movimientos.data, saldo)

  return (
    <>
      {barra}
      {/* ⚠ **El fallo del borrado se DICE.** Sin esto, pulsar la papelera sobre un
          movimiento que el servidor no deja borrar no hacía nada visible: la fila
          seguía ahí, anulada, y quien lo intentaba concluía que «sólo se anula». Un
          409 silencioso es peor que un error: parece que el sistema hizo otra cosa. */}
      {borrar.error instanceof ErrorApi && (
        <p
          role="alert"
          className="mb-2 flex items-start gap-2 text-sm"
          style={{ color: "var(--destructive)" }}
        >
          <AlertCircle size={16} className="mt-0.5 shrink-0" aria-hidden={true} />
          {borrar.error.message}
        </p>
      )}
      <div ref={tablaExtracto}>
        <Tabla etiqueta={`Extracto de ${cuenta.nombre}`}>
          <Cabecera>
            <Th derecha>Fecha</Th>
            <Th>Concepto</Th>
            <Th derecha>Entrada</Th>
            <Th derecha>Salida</Th>
            <Th derecha>Saldo</Th>
            {(puedeAnular || puedeEditar || puedeEliminar) && (
              <Th>
                <span className="sr-only">Acciones</span>
              </Th>
            )}
          </Cabecera>
          <Cuerpo>
            {lineas.map((l) => {
              const entra = l.tipo === "INGRESO"
              const tachado = l.anulado
                ? { opacity: 0.55, textDecoration: "line-through" as const }
                : undefined
              return (
                <Fila
                  key={l.id}
                  data-testid={`extracto-${l.id}`}
                  className="cursor-pointer"
                  onClick={() => setAbierto(l)}
                >
                  {/* ⚠ La del BANCO. Cuándo se tecleó la fila está en `creado_en`, que
                    sigue en la ficha y en la bitácora; aquí manda cuándo pasó. */}
                  <Td derecha numero style={{ color: "var(--fg-muted)" }}>
                    <span title={`Registrado el ${comoFechaHora(l.creado_en)}`}>
                      {comoFecha(l.fecha_valor)}
                    </span>
                  </Td>
                  <Td>
                    <span style={tachado}>{l.concepto}</span>
                    {l.anulado && (
                      <span className="ml-2">
                        <Insignia tono="peligro">Anulada</Insignia>
                      </span>
                    )}
                    {l.referencia && (
                      <span className="tabular block text-xs" style={{ color: "var(--fg-muted)" }}>
                        {l.referencia}
                      </span>
                    )}
                    {/* El soporte por endpoint autenticado, nunca como estático. */}
                    {l.soportes?.map((s) => (
                      <button
                        type="button"
                        key={s.id}
                        onClick={(evento) => {
                          evento.stopPropagation()
                          setViendo({
                            ruta: `/api/v1/transacciones/${l.id}/soportes/${s.id}/contenido`,
                            nombre: s.nombre_original,
                          })
                        }}
                        className="foco-cristal flex items-center gap-1 text-left text-xs underline"
                        style={{ color: "var(--fg-muted)" }}
                      >
                        <Paperclip size={12} aria-hidden={true} />
                        {s.nombre_original}
                      </button>
                    ))}
                    {l.anulado && l.motivo_anulacion && (
                      <span className="block text-xs" style={{ color: "var(--fg-muted)" }}>
                        {l.motivo_anulacion}
                      </span>
                    )}
                  </Td>
                  <Td derecha numero>
                    {entra ? (
                      <span style={{ color: "var(--success)", ...tachado }}>{monto(l.monto)}</span>
                    ) : (
                      <span style={{ color: "var(--fg-muted)" }}>—</span>
                    )}
                  </Td>
                  <Td derecha numero>
                    {entra ? (
                      <span style={{ color: "var(--fg-muted)" }}>—</span>
                    ) : (
                      <span style={{ color: "var(--destructive)", ...tachado }}>
                        {monto(l.monto)}
                      </span>
                    )}
                  </Td>
                  <Td derecha numero>
                    {l.saldoDespues === null ? (
                      <span style={{ color: "var(--fg-muted)" }}>—</span>
                    ) : (
                      <span className="font-medium">{comoMonto(l.saldoDespues)}</span>
                    )}
                  </Td>
                  {(puedeAnular || puedeEditar || puedeEliminar) && (
                    <Td derecha>
                      <span className="flex justify-end gap-1">
                        {puedeEditar && l.editable && (
                          <button
                            type="button"
                            onClick={(evento) => {
                              evento.stopPropagation()
                              setEditando(l)
                            }}
                            aria-label={`Editar «${l.concepto}»`}
                            className="foco-cristal toque-cristal inline-flex rounded-[var(--radius-control)] p-1.5"
                            style={{ color: "var(--fg-muted)" }}
                          >
                            <Pencil size={16} aria-hidden={true} />
                          </button>
                        )}
                        {puedeAnular && !l.anulado && (
                          <button
                            type="button"
                            onClick={(evento) => {
                              evento.stopPropagation()
                              setAnulando(l)
                            }}
                            aria-label={`Anular «${l.concepto}»`}
                            className="foco-cristal toque-cristal inline-flex rounded-[var(--radius-control)] p-1.5"
                            style={{ color: "var(--fg-muted)" }}
                          >
                            <Ban size={16} aria-hidden={true} />
                          </button>
                        )}
                        {puedeEliminar && (
                          <button
                            type="button"
                            onClick={(evento) => {
                              evento.stopPropagation()
                              borrar.mutate(l)
                            }}
                            disabled={borrar.isPending}
                            aria-label={`Eliminar «${l.concepto}»`}
                            className="foco-cristal toque-cristal inline-flex rounded-[var(--radius-control)] p-1.5"
                            style={{ color: "var(--destructive)" }}
                          >
                            <Trash2 size={16} aria-hidden={true} />
                          </button>
                        )}
                      </span>
                    </Td>
                  )}
                </Fila>
              )
            })}
          </Cuerpo>
        </Tabla>
      </div>
      {/* ⚠ **Una tanda más, no una página numerada.** Un ledger crece sin parar: con
          páginas habría que contar la tabla entera en cada carga —más caro cada año—
          para enseñar un número que a nadie le dice nada. */}
      {lineas.length >= tope && tope < TOPE_SERVIDOR && (
        <div className="mt-3 flex justify-center">
          <Boton
            variante="secundario"
            onClick={() => setTope((t) => Math.min(t + POR_TANDA, TOPE_SERVIDOR))}
            disabled={movimientos.isFetching}
          >
            {movimientos.isFetching ? "Trayendo…" : "Ver más"}
          </Boton>
        </div>
      )}
      <p className="mt-2 text-xs" style={{ color: "var(--fg-muted)" }}>
        {lineas.length} movimiento{lineas.length === 1 ? "" : "s"}, del más reciente al más antiguo,
        en {divisa}.
        {lineas.length >= TOPE_SERVIDOR &&
          ` Son los ${TOPE_SERVIDOR} más recientes: para ir más atrás, elige un mes o búscalo en «Movimientos».`}
        {hayFiltros &&
          " El saldo corrido sólo sale sin filtros: se calcula restando desde el saldo de hoy, y eso pide los movimientos seguidos."}
      </p>

      <VistaPreviaArchivo archivo={viendo} onCerrar={() => setViendo(null)} />
      {abierto && <DetalleMovimiento movimiento={abierto} onCerrar={() => setAbierto(null)} />}
      <DialogoAnular movimiento={anulando} divisa={divisa} onCerrar={() => setAnulando(null)} />
      <DialogoEditarMovimiento movimiento={editando} onCerrar={() => setEditando(null)} />
      {confirmacion}
    </>
  )
}
